import React, { useState, useEffect } from 'react';
import Logo from "../images/logo/Quick-Care.png";
import SearchIcon from '@mui/icons-material/Search';
import Profile from "../images/avatar.png";
import MenuIcon from '@mui/icons-material/Menu';
import ClearIcon from '@mui/icons-material/Clear';
import { Link } from 'react-router-dom';
import { getToken, clearToken } from '../utils/tokenHelper';

const Header = ({ isSidebarOpen, toggleSidebar, title }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [copySuccess, setCopySuccess] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const token = getToken();
        setIsLoggedIn(!!token);

        if (token) {
            // Fetch profile data
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow",
            };

            fetch("https://devapi.quickcare.ng/api/v1/user/profile", requestOptions)
                .then((response) => response.json())
                .then((result) => {
                    if (result.success) {
                        setProfile(result.data);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching profile data:", error);
                });
        }
    }, []);

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
        setCopySuccess(''); // Reset copy success message when modal opens/closes
    };

    const handleLogout = () => {
        clearToken();
        window.location.reload(); // Reload the page after clearing the token
    };

    const getInitials = () => {
        if (profile) {
            const { first_name, last_name } = profile;
            return `${first_name?.charAt(0)}${last_name?.charAt(0)}`;
        }
        return '';
    };

    const handleCopyReferralCode = async () => {
        if (profile?.referral_code) {
            try {
                await navigator.clipboard.writeText(profile.referral_code);
                setCopySuccess('Copied!');
                setTimeout(() => setCopySuccess(''), 2000);
            } catch (err) {
                console.error('Failed to copy text: ', err);
                setCopySuccess('Failed to copy');
                setTimeout(() => setCopySuccess(''), 2000);
            }
        }
    };

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isModalOpen && !event.target.closest('.profile-modal') && !event.target.closest('.profile-button')) {
                setIsModalOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isModalOpen]);

    return (
        <div className='w-full flex flex-row items-center justify-between py-3 px-3 lg:px-8 md:pb-4 bg-white md:border-b-2 instrument-sans-semibold'>
            <div className='md:hidden' onClick={toggleSidebar}>
                {isSidebarOpen ? <ClearIcon /> : <MenuIcon />}
            </div>

            <Link to='/' className='lg:hidden'>
                <img src={Logo} alt="Logo" className='w-32 h-15'/>
            </Link>
            
            <div className='hidden md:block'>
                <h1 className='text-2xl font-semibold text-[#828282]'>
                    {title}
                </h1>
            </div>

            <div className="relative">
                <button
                    onClick={toggleModal}
                    className="profile-button flex items-center justify-center w-14 h-14 rounded-full overflow-hidden bg-gray-200 hover:opacity-80 transition-opacity"
                >
                    {profile ? (
                        profile.avatar ? (
                            <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center w-full h-full text-white bg-gray-800 text-xl font-semibold">
                                {getInitials()}
                            </div>
                        )
                    ) : (
                        <img src={Profile} alt="Profile" className="w-full h-full object-cover" />
                    )}
                </button>

                {/* Modal */}
                {isModalOpen && profile && (
                    <div className="profile-modal absolute right-0 mt-2 z-50 p-4 bg-white rounded-md shadow-lg min-w-[280px] border border-gray-200">
                        <div className="mb-3 pb-2 border-b border-gray-200">
                            <h3 className="font-semibold text-lg">Profile Information</h3>
                        </div>
                        
                        <div className="space-y-2">
                            <p className="text-sm">
                                <span className='mr-1 font-medium'>Name:</span> 
                                <span className="text-gray-700">{`${profile.first_name || ''} ${profile.last_name || ''}`}</span>
                            </p>
                            
                            <p className="text-sm">
                                <span className='mr-1 font-medium'>Email:</span> 
                                <span className="text-gray-700">{profile.email || 'Not provided'}</span>
                            </p>
                            
                            <p className="text-sm">
                                <span className='mr-1 font-medium'>Phone no:</span> 
                                <span className="text-gray-700">+{profile.phone_number || 'Not provided'}</span>
                            </p>
                            
                            <p className="text-sm">
                                <span className='mr-1 font-medium'>Subscription Status:</span>
                                {profile.is_subscribed ? (
                                    <span className="text-green-600"> Subscribed</span>
                                ) : (
                                    <span className="text-red-600"> Not Subscribed</span>
                                )}
                            </p>
                            
                            <p className="text-sm capitalize">
                                <span className='mr-1 font-medium'>Country:</span> 
                                <span className="text-gray-700">{profile.country || 'Not provided'}</span>
                            </p>
                            
                            {/* Clickable Referral Code */}
                            <div className='flex items-center gap-2'>
                                <p className="text-sm">
                                    <span className='font-medium'>Referral code:</span>
                                </p>
                                <button
                                    onClick={handleCopyReferralCode}
                                    className="relative px-2 py-1 text-blue-600 transition-colors bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none group text-sm"
                                    title="Click to copy referral code"
                                >
                                    <span>{profile.referral_code || 'N/A'}</span>
                                    {copySuccess && (
                                        <span className="absolute px-2 py-1 text-xs text-white bg-green-500 rounded-md -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                            {copySuccess}
                                        </span>
                                    )}
                                </button>
                            </div>
                            
                            <div className='flex items-center justify-between gap-2'>
                                <div>
                                    <p className="text-sm">
                                        <span className='font-medium'>Wallet Balance:</span> 
                                        <span className="text-gray-700 ml-1">₦{profile.wallet_balance?.toLocaleString() || '0'}</span>
                                    </p>
                                </div>
                                <Link 
                                    to='/withdraw' 
                                    className='text-sm text-blue-500 hover:text-blue-700'
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Withdraw
                                </Link>
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-2 border-t border-gray-200">
                            <button
                                onClick={handleLogout}
                                className="w-full px-3 py-2 text-sm text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Header;